import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useRaffle } from '../hooks/useRaffle';
import { useUserEntries } from '../hooks/useUserEntries';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../lib/utils';
import { Button, Spinner, StatusBadge, CategoryBadge, ConditionBadge, PriceDisplay } from '../components/ui';
import LocationBadge from '../components/shared/LocationBadge';
import { CompetitionSEO } from '../components/shared/SEO';
import CountdownTimer from '../components/raffle/CountdownTimer';
import { ThresholdProgressBar } from '../components/raffle/ProgressBar';
import { OddsCard } from '../components/raffle/OddsDisplay';
import SellerCard from '../components/raffle/SellerCard';
import EntryModal, { useEntryModal } from '../components/raffle/EntryModal';
import WinnerBanner from '../components/raffle/WinnerBanner';
import HeatMeter from '../components/raffle/HeatMeter';
import ShareGraphicModal from '../components/raffle/ShareGraphicModal';
import { setReturnUrl } from '../lib/storage';

/**
 * RaffleDetail Page
 *
 * Public competition view with entry capability.
 * Post-draw management (messaging, handover, etc.) is on /competition/:id/manage
 */
export default function RaffleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isPhoneVerified, user } = useAuth();
  const { raffle, loading, error } = useRaffle(id, user?.uid);
  const { entries: userEntries } = useUserEntries(id);
  const { isOpen, openModal, closeModal } = useEntryModal();

  const [selectedImage, setSelectedImage] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);

  // Calculate user's ticket count from their entries
  const userTicketCount = userEntries.reduce((sum, entry) => sum + entry.ticketCount, 0);

  // Calculate user's remaining ticket allowance (max 20% of total, capped at 10)
  const MAX_TICKETS_ABSOLUTE = 10;
  const MAX_PERCENT_OF_TOTAL = 0.20;

  // Handle enter raffle click
  const handleEnterRaffle = () => {
    if (!isAuthenticated) {
      setReturnUrl(`/competition/${id}`);
      navigate('/auth/login');
      return;
    }

    if (!isPhoneVerified) {
      setReturnUrl(`/competition/${id}`);
      navigate('/auth/verify-phone');
      return;
    }

    openModal();
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Error state
  if (error || !raffle) {
    return (
      <div className="min-h-screen bg-midnight">
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-display font-bold text-cream mb-4">
            Competition Not Found
          </h1>
          <p className="text-cream/60 mb-8">
            This competition may have been removed or doesn't exist.
          </p>
          <Link to="/browse">
            <Button>Browse Competitions</Button>
          </Link>
        </div>
      </div>
    );
  }

  const {
    title,
    description,
    photos = [],
    category,
    condition,
    status,
    ticketPrice,
    ticketQuantity,
    ticketsSold = 0,
    minimumThreshold = 50,
    pickupArea,
    endsAt,
    seller,
    sellerId,
    winnerId,
  } = raffle;

  const isActive = status === 'active';
  const isCompleted = status === 'completed';
  const isCancelled = status === 'cancelled';
  const isSoldOut = ticketsSold >= ticketQuantity;

  // Role detection
  const isOwnRaffle = user?.uid === sellerId;
  const isWinner = isCompleted && user?.uid === winnerId;
  const isSellerPostDraw = isCompleted && isOwnRaffle;

  const ticketsRemaining = ticketQuantity - ticketsSold;

  // Calculate user's remaining ticket allowance
  const maxTicketsAllowed = Math.min(
    MAX_TICKETS_ABSOLUTE,
    Math.floor(ticketQuantity * MAX_PERCENT_OF_TOTAL)
  );
  const userRemainingAllowance = Math.max(0, maxTicketsAllowed - userTicketCount);
  const hasReachedLimit = userRemainingAllowance === 0 && userTicketCount > 0;

  const canEnter = isActive && !isSoldOut && !isOwnRaffle && !hasReachedLimit;

  // Calculate earnings for seller view (95% after 5% platform fee)
  const totalRevenue = ticketsSold * ticketPrice;
  const platformFee = totalRevenue * 0.05;
  const sellerEarnings = (totalRevenue - platformFee) / 100;

  return (
    <div className="min-h-screen bg-midnight">
      <CompetitionSEO competition={raffle} path={`/competition/${id}`} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-32 lg:pb-8">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm">
          <ol className="flex items-center gap-2 text-cream/50">
            <li>
              <Link to="/browse" className="hover:text-primary transition-colors">
                Browse
              </Link>
            </li>
            <li>/</li>
            <li className="text-cream font-medium truncate max-w-[200px]">
              {title}
            </li>
          </ol>
        </nav>

        {/* Winner Banner - Full celebration with confetti */}
        {isWinner && (
          <div className="mb-8">
            <WinnerBanner
              ticketNumber={userEntries.flatMap((e) => e.ticketNumbers || []).find(() => true) || 1}
              raffleTitle={title}
              showConfetti
              onConfirmCollection={() => navigate(`/competition/${id}/manage`)}
              primaryButtonLabel="Manage My Prize"
            />
          </div>
        )}

        {/* Seller Success Banner - Links to manage page */}
        {isSellerPostDraw && !isWinner && (
          <Link to={`/competition/${id}/manage`}>
            <div className="mb-8 bg-gradient-to-r from-success/20 to-accent/20 border border-success/30 rounded-xl p-5 hover:border-success/50 transition-colors cursor-pointer">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-success/20 flex items-center justify-center">
                    <span className="text-2xl">🎉</span>
                  </div>
                  <div>
                    <h2 className="text-lg font-display font-bold text-success">
                      You Have a Winner!
                    </h2>
                    <p className="text-cream/70 text-sm">
                      Coordinate handover and receive your payout
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-primary">
                  <span className="text-sm font-medium">Manage</span>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Cancelled Banner */}
        {isCancelled && (
          <div className="mb-8 bg-error/10 border border-error/30 rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-error/20 flex items-center justify-center">
                <CancelledIcon className="w-7 h-7 text-error" />
              </div>
              <div>
                <h2 className="text-xl font-display font-bold text-error">
                  Competition Cancelled
                </h2>
                <p className="text-cream/70">
                  This competition didn't reach the minimum threshold. All entries have been refunded.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="lg:grid lg:grid-cols-2 lg:gap-12">
          {/* Image Gallery */}
          <div className="mb-8 lg:mb-0">
            {/* Main Image */}
            <div className="aspect-square rounded-xl overflow-hidden bg-midnight-lighter mb-4 shadow-card">
              {photos[selectedImage] ? (
                <img
                  src={photos[selectedImage]}
                  alt={`${title} - Image ${selectedImage + 1}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-cream/30">
                  <PlaceholderIcon className="w-24 h-24" />
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {photos.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-4 pt-1 px-1 -mx-1">
                {photos.map((photo, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`
                      flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden transition-all
                      ${selectedImage === index
                        ? 'ring-2 ring-primary ring-offset-2 ring-offset-midnight'
                        : 'ring-1 ring-cream/20 hover:ring-cream/40'
                      }
                    `}
                  >
                    <img
                      src={photo}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            {/* Status badges */}
            <div className="flex items-center gap-2 flex-wrap mb-4">
              <CategoryBadge category={category} />
              <ConditionBadge condition={condition} />
              <StatusBadge status={status} endsAt={endsAt} />
            </div>

            {/* Title and Heat Meter */}
            <div className="flex items-start justify-between gap-4 mb-2">
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-cream">
                {title}
              </h1>
              {isActive && (
                <HeatMeter raffle={raffle} size="md" />
              )}
            </div>

            {/* Location */}
            <div className="mb-6">
              <LocationBadge location={pickupArea} size="md" showIcon />
            </div>

            {/* Price card */}
            <div className="bg-midnight-light border border-primary/20 rounded-xl p-6 mb-6">
              <div className="flex items-baseline justify-between mb-4">
                <div className="flex items-baseline gap-1">
                  <PriceDisplay amount={ticketPrice} cents size="xl" className="text-primary" />
                  <span className="text-cream/50">/entry</span>
                </div>
                {isActive && endsAt && (
                  <CountdownTimer endDate={endsAt} />
                )}
              </div>

              <ThresholdProgressBar
                sold={ticketsSold}
                total={ticketQuantity}
                threshold={minimumThreshold}
                className="mb-4"
              />

              <div className="flex justify-between text-sm text-cream/60 mb-6">
                <span className="font-mono">{ticketsRemaining} entries remaining</span>
                <span className="font-mono">
                  Total: {formatCurrency(ticketPrice * ticketQuantity)}
                </span>
              </div>

              {/* User's current odds (if they have entries) */}
              {isAuthenticated && userTicketCount > 0 && isActive && (
                <OddsCard
                  userTickets={userTicketCount}
                  totalTickets={ticketsSold}
                  dark
                  className="mb-4"
                />
              )}

              {/* Seller Dashboard (when viewing own raffle) */}
              {isOwnRaffle ? (
                <div className="space-y-4">
                  <div className="bg-midnight border border-accent/30 rounded-lg p-4">
                    <p className="text-accent text-sm font-medium flex items-center gap-2 mb-3">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      This is your competition
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-midnight-lighter rounded-lg p-3 text-center">
                        <p className="text-2xl font-mono font-bold text-primary">{ticketsSold}</p>
                        <p className="text-xs text-cream/50">Entries Sold</p>
                      </div>
                      <div className="bg-midnight-lighter rounded-lg p-3 text-center">
                        <p className="text-2xl font-mono font-bold text-accent">€{sellerEarnings.toFixed(2)}</p>
                        <p className="text-xs text-cream/50">You'll Receive (after 5% fee)</p>
                      </div>
                    </div>
                  </div>

                  {/* Share buttons */}
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-cream/70">Share your competition</p>
                    <div className="grid grid-cols-3 gap-2">
                      {/* Instagram Story */}
                      <button
                        onClick={() => setShowShareModal(true)}
                        className="flex flex-col items-center gap-1.5 p-3 rounded-lg bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] hover:opacity-90 transition-opacity"
                      >
                        <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                        <span className="text-xs font-medium text-white">Story</span>
                      </button>

                      {/* X/Twitter */}
                      <button
                        onClick={() => {
                          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out my competition on Raflr: ${title}`)}&url=${encodeURIComponent(window.location.href)}`, '_blank');
                        }}
                        className="flex flex-col items-center gap-1.5 p-3 rounded-lg bg-midnight-lighter hover:bg-midnight-light border border-cream/10 transition-colors"
                      >
                        <svg className="w-6 h-6 text-cream" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                        <span className="text-xs font-medium text-cream/70">X</span>
                      </button>

                      {/* Copy Link */}
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(window.location.href);
                          alert('Link copied!');
                        }}
                        className="flex flex-col items-center gap-1.5 p-3 rounded-lg bg-midnight-lighter hover:bg-midnight-light border border-cream/10 transition-colors"
                      >
                        <svg className="w-6 h-6 text-cream" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        <span className="text-xs font-medium text-cream/70">Link</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : canEnter ? (
                <>
                  <Button fullWidth size="lg" onClick={handleEnterRaffle}>
                    {userTicketCount > 0 ? 'Buy More Entries' : 'Enter Competition'}
                  </Button>
                  {isAuthenticated && userRemainingAllowance < maxTicketsAllowed && (
                    <p className="text-sm text-cream/50 text-center mt-2">
                      You can buy up to {userRemainingAllowance} more {userRemainingAllowance !== 1 ? 'entries' : 'entry'}
                    </p>
                  )}
                </>
              ) : hasReachedLimit ? (
                <div className="space-y-2">
                  <Button fullWidth size="lg" disabled>
                    Entry Limit Reached
                  </Button>
                  <p className="text-sm text-cream/50 text-center">
                    You've purchased the maximum {maxTicketsAllowed} entries for this competition
                  </p>
                </div>
              ) : isSoldOut ? (
                <Button fullWidth size="lg" disabled>
                  Sold Out
                </Button>
              ) : isCompleted ? (
                <Button fullWidth size="lg" disabled>
                  Competition Ended
                </Button>
              ) : (
                <Button fullWidth size="lg" disabled>
                  Competition Ended
                </Button>
              )}

              {!isAuthenticated && !isOwnRaffle && isActive && !isSoldOut && (
                <p className="text-sm text-cream/50 text-center mt-3">
                  You'll need to sign in to enter
                </p>
              )}
            </div>

            {/* Description */}
            <div className="bg-midnight-light rounded-xl p-6 mb-6">
              <h2 className="text-lg font-semibold text-cream mb-3">
                Description
              </h2>
              <div className="text-cream/70 space-y-2">
                {description.split('\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>

            {/* Seller info */}
            {seller && (
              <div className="bg-midnight-light rounded-xl p-6">
                <h2 className="text-lg font-semibold text-cream mb-4">
                  Seller
                </h2>
                <SellerCard seller={seller} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile sticky footer */}
      {canEnter && !isOwnRaffle && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-midnight-light border-t border-cream/10 lg:hidden safe-bottom">
          <div className="flex items-center justify-between gap-4">
            <div>
              <span className="text-xl font-mono font-bold text-primary">
                {formatCurrency(ticketPrice)}
              </span>
              <span className="text-cream/50">/entry</span>
            </div>
            <Button onClick={handleEnterRaffle}>
              {userTicketCount > 0 ? 'Buy More' : 'Enter'}
            </Button>
          </div>
        </div>
      )}

      {/* Entry Modal */}
      {raffle && (
        <EntryModal
          isOpen={isOpen}
          onClose={closeModal}
          raffle={raffle}
        />
      )}

      {/* Share Graphic Modal */}
      {raffle && (
        <ShareGraphicModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          raffle={{ ...raffle, id }}
        />
      )}
    </div>
  );
}

// Icon components
function PlaceholderIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

function LocationIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function CancelledIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
      />
    </svg>
  );
}

function ArrowRightIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}
